import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { computed, inject, Injectable, makeStateKey, PLATFORM_ID, signal, TransferState } from '@angular/core';

const BACKGROUND_INDEX_KEY = makeStateKey<number>('backgroundIndex');

@Injectable({ providedIn: 'root' })
export class AppStateService {
  readonly #platformId = inject(PLATFORM_ID);
  readonly #transferState = inject(TransferState);
  readonly numberOfBackgrounds = 5;

  readonly backgroundIndex = signal<number>(this.getBackgroundIndex());
  readonly supportsWebp = signal(false);

  readonly videoSrc = computed(() => `assets/video/ai_background${this.backgroundIndex()}.mp4`);
  readonly imageSrc = computed(() => `assets/img/app/ai_background${this.backgroundIndex()}.webp`);
  readonly imageFallbackSrc = computed(() => `assets/img/app/ai_background${this.backgroundIndex()}.png`);
  readonly loginImage = computed(() => (this.supportsWebp() ? this.imageSrc() : this.imageFallbackSrc()));

  constructor() {
    if (isPlatformBrowser(this.#platformId)) {
      this.supportsWebp.set(this.checkSupportsWebp());
    }
  }

  private getBackgroundIndex(): number {
    if (isPlatformServer(this.#platformId)) {
      const index = this.getRandomBackgroundIndex();
      this.#transferState.set(BACKGROUND_INDEX_KEY, index);
      return index;
    } else if (isPlatformBrowser(this.#platformId)) {
      const storedIndex = this.#transferState.get(BACKGROUND_INDEX_KEY, null);
      if (storedIndex !== null) {
        this.#transferState.remove(BACKGROUND_INDEX_KEY);
        return storedIndex;
      } else {
        const newIndex = this.getRandomBackgroundIndex();
        return newIndex;
      }
    }
    return this.getRandomBackgroundIndex();
  }

  private getRandomBackgroundIndex(): number {
    return Math.floor(Math.random() * this.numberOfBackgrounds) + 1;
  }

  private checkSupportsWebp(): boolean {
    try {
      const elem = document.createElement('canvas');
      if (elem.getContext && elem.getContext('2d')) {
        return elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
      }
      return false;
    } catch {
      return false;
    }
  }
}
